import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { heroSlides } from '../mock';

const SLIDE_INTERVAL = 6000;

const SlideMedia = ({ slide, active }) => (
  <div className={`absolute inset-0 transition-opacity duration-[1400ms] ease-in-out ${active ? 'opacity-100' : 'opacity-0'}`}>
    <img
      src={slide.image}
      alt={slide.title}
      className={`w-full h-full object-cover transition-transform duration-[8000ms] ease-out ${active ? 'scale-105' : 'scale-100'}`}
    />
    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70"></div>
  </div>
);

const SlideContent = ({ slide }) => (
  <div className="max-w-3xl">
    <p className="text-white/85 text-xs md:text-sm tracking-[0.4em] uppercase mb-6 font-light">
      {slide.kicker}
    </p>
    <h1 className="font-serif text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] mb-6">
      {slide.title}
    </h1>
    <p className="text-white/85 text-base md:text-lg font-light max-w-xl mb-10 leading-relaxed">
      {slide.subtitle}
    </p>
    <div className="flex flex-wrap items-center gap-4">
      <a href="#gallery" className="inline-flex items-center px-7 py-3.5 text-xs tracking-[0.25em] uppercase bg-[#faf7f2] text-[#3A4A2C] hover:bg-[#C87355] hover:text-white transition-colors duration-300">
        Scopri il Casale
      </a>
      <a href="#contact" className="inline-flex items-center px-7 py-3.5 text-xs tracking-[0.25em] uppercase border border-white text-white hover:bg-white hover:text-[#3A4A2C] transition-colors duration-300">
        Prenota Ora
      </a>
    </div>
  </div>
);

const HeroControls = ({ idx, total, onSelect, onPrev, onNext }) => (
  <div className="flex items-center justify-between mt-14">
    <div className="flex items-center gap-3">
      {heroSlides.map((s, i) => (
        <button
          key={s.id}
          onClick={() => onSelect(i)}
          className={`h-[2px] transition-all duration-500 ${i === idx ? 'w-14 bg-white' : 'w-8 bg-white/40'}`}
          aria-label={`slide-${i}`}
        />
      ))}
    </div>
    <div className="hidden md:flex items-center gap-3">
      <button onClick={onPrev} className="w-11 h-11 rounded-full border border-white/50 flex items-center justify-center text-white hover:bg-white hover:text-[#3A4A2C] transition-colors" aria-label="prev">
        <ChevronLeft size={18} />
      </button>
      <button onClick={onNext} className="w-11 h-11 rounded-full border border-white/50 flex items-center justify-center text-white hover:bg-white hover:text-[#3A4A2C] transition-colors" aria-label="next">
        <ChevronRight size={18} />
      </button>
    </div>
  </div>
);

const Hero = () => {
  const [idx, setIdx] = useState(0);
  const total = heroSlides.length;

  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % total), SLIDE_INTERVAL);
    return () => clearInterval(t);
  }, [total]);

  const go = useCallback((i) => setIdx(((i % total) + total) % total), [total]);
  const prev = useCallback(() => setIdx(i => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIdx(i => (i + 1) % total), [total]);

  return (
    <section id="home" className="relative h-screen w-full overflow-hidden">
      {heroSlides.map((s, i) => (
        <SlideMedia key={s.id} slide={s} active={i === idx} />
      ))}

      <div className="relative z-10 h-full flex flex-col justify-end pb-24 md:pb-28 max-w-7xl mx-auto px-6 lg:px-10">
        <SlideContent slide={heroSlides[idx]} />
        <HeroControls idx={idx} total={total} onSelect={go} onPrev={prev} onNext={next} />
      </div>

      <div className="absolute right-6 top-1/2 -translate-y-1/2 z-10 hidden lg:flex flex-col items-center gap-3 text-white/70">
        <span className="text-[10px] tracking-[0.4em] uppercase [writing-mode:vertical-rl]">Scroll</span>
        <div className="w-px h-16 bg-white/50 animate-pulse"></div>
      </div>
    </section>
  );
};

export default Hero;
